import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import { apiFetch, downloadPdf } from '../../api';
import * as styles from './PackagingSlip.styles';
import DateTimeText from '../../components/DateTimeText';
import { FaTrash } from 'react-icons/fa';
import Select from "react-select";

export default function PackagingSlip() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const PAGE_SIZE = 10;

  const [visibleItems, setVisibleItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  /* =========================
     HEADER
     ========================= */
  // Helper function to get current datetime in local timezone for datetime-local input
  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [header, setHeader] = useState({
    invoice_no: '',
    invoice_date: getCurrentDateTime(),
    customer_name: '',
    order_no: '',
    order_date: '',
    address: '',
    transport_mode: '',
    status: 'submit',
  });

  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setHeader((p) => ({ ...p, [name]: value }));
  };

  /* =========================
     WEIGHT CALCULATIONS
     ========================= */
  const calculateNetWeight = (boxIndex) => {
    const box = boxes[boxIndex];
    if (!box || !box.items) return 0;
    
    return box.items.reduce((total, item) => {
      return total + (item.total_weight || 0);
    }, 0);
  };

  const updateBoxWeights = (boxIndex) => {
    setBoxes(prevBoxes => {
      const newBoxes = [...prevBoxes];
      const netWeight = calculateNetWeight(boxIndex);
      const additionalWeight = newBoxes[boxIndex].additional_weight || 0;
      
      newBoxes[boxIndex] = {
        ...newBoxes[boxIndex],
        net_weight: netWeight,
        gross_weight: netWeight + additionalWeight,
      };
      
      return newBoxes;
    });
  };

const handleAdditionalWeightChange = (boxIndex, value) => {

  setBoxes(prevBoxes => {
    const newBoxes = [...prevBoxes];
    const netWeight = newBoxes[boxIndex].net_weight || 0;

    // allow empty input
    const additionalWeight = value === "" ? "" : parseFloat(value);

    newBoxes[boxIndex] = {
      ...newBoxes[boxIndex],
      additional_weight: additionalWeight,
      gross_weight: value === "" ? netWeight : netWeight + additionalWeight,
    };

    return newBoxes;
  });
};

  /* =========================
     BOX DETAILS
     ========================= */
  const [boxInfo, setBoxInfo] = useState({
    box_count: 1,
  });

  const handleBoxChange = (e) => {
    const { name, value } = e.target;
    const newValue = name === 'box_count' ? Math.max(1, parseInt(value) || 1) : value;
    
    setBoxInfo((p) => {
      const updated = { ...p, [name]: newValue };
      if (name === 'box_count') {
        const newCount = parseInt(newValue);
        const currentCount = boxes.length;
        
        if (newCount > currentCount) {
          const newBoxes = [...boxes];
          for (let i = currentCount; i < newCount; i++) {
            newBoxes.push({
              boxNumber: i + 1,
              length: '',
              width: '',
              height: '',
              net_weight: 0,
              additional_weight: 0,
              gross_weight: 0,
              box_type: '',
              items: [],
            });
          }
          setBoxes(newBoxes);
        } else if (newCount < currentCount) {
          setBoxes(boxes.slice(0, newCount));
          if (activeBoxIndex >= newCount) {
            setActiveBoxIndex(newCount - 1);
          }
        }
      }
      
      return updated;
    });
  };

  /* =========================
     BOXES STATE (Multiple boxes with their own items and dimensions)
     ========================= */
  const [boxes, setBoxes] = useState([
    {
      boxNumber: 1,
      length: '',
      width: '',
      height: '',
      net_weight: 0,
      additional_weight: 0,
      gross_weight: 0,
      box_type: '',
      items: [],
    }
  ]);

  /* =========================
     ACTIVE BOX STATE (Which box we're currently scanning into)
     ========================= */
  const [activeBoxIndex, setActiveBoxIndex] = useState(0);

  /* =========================
     SCAN STATE
     ========================= */
  const [scanMode, setScanMode] = useState('ITEM');
  const [activeItemIndex, setActiveItemIndex] = useState(null);

  /* =========================
     ITEM MASTER
     ========================= */
  const [itemList, setItemList] = useState([]);

  useEffect(() => {
    apiFetch('/api/item-master/fetch-item-list').then(setItemList);
  }, []);

  /* =========================
  SAVE STATE
  ========================= */
  const [isSaving, setIsSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  /* =========================
    CHECK IF EDITABLE
  ========================= */
  const isEditable = !isEdit || !['submit', 'cancel'].includes(header.status);
  const canCancel = isEdit && header.status === 'submit';

  /* =========================
     BARCODE BUFFER
     ========================= */
  const bufferRef = useRef('');
  const timeoutRef = useRef(null);
  const processingRef = useRef(false);
  const invoiceDateRef = useRef(null);
  const orderDateRef = useRef(null);

  /* =========================
     HANDLE BOX DIMENSION CHANGE
     ========================= */
  const handleBoxDimensionChange = (index, e) => {
    const { name, value } = e.target;
    
    setBoxes((prevBoxes) => {
      const updatedBoxes = [...prevBoxes];
      updatedBoxes[index] = {
        ...updatedBoxes[index],
        [name]: value,
      };
      return updatedBoxes;
    });
  };

  const handleBoxTypeChange = (boxIndex, value) => {
    setBoxes((prevBoxes) => {
      const newBoxes = [...prevBoxes];
      newBoxes[boxIndex] = {
        ...newBoxes[boxIndex],
        box_type: value,
      };
      return newBoxes;
    });
  };

  /* =========================
     ITEM SCAN (for active box)
     ========================= */
  const processItemScan = useCallback(async (itemCode) => {
    try {
      const res = await apiFetch(`/api/item-master/find-item?code=${itemCode}`);

      if (!res?.success) {
        alert('Item not found');
        return;
      }

      const item = {
        item_code: res.item_code,
        uom: res.uom,
        weight: Number(res.weight),
      };

      setBoxes((prevBoxes) => {
        const updatedBoxes = [...prevBoxes];
        const currentBox = updatedBoxes[activeBoxIndex];
        if (!currentBox) return prevBoxes;
        const currentItems = currentBox.items || [];
        const existingIndex = currentItems.findIndex(
          (r) => r.item_code === item.item_code && !r.is_manual
        );

        let newActiveItemIndex = null;

        if (existingIndex !== -1) {
          newActiveItemIndex = existingIndex;
          const updatedItems = [...currentItems];
          updatedItems[existingIndex] = {
            ...updatedItems[existingIndex],
            qty: updatedItems[existingIndex].qty + 1,
            total_weight: (updatedItems[existingIndex].qty + 1) * updatedItems[existingIndex].weight,
          };
          
          updatedBoxes[activeBoxIndex] = {
            ...currentBox,
            items: updatedItems,
          };
        } else {
          newActiveItemIndex = currentItems.length;
          const newItem = {
            ...item,
            qty: 1,
            total_weight: item.weight,
            lot_code: '',
            is_manual: false,
          };
          
          updatedBoxes[activeBoxIndex] = {
            ...currentBox,
            items: [...currentItems, newItem],
          };
        }

        setActiveItemIndex(newActiveItemIndex);
        setScanMode('LOT');
        
        // Recalculate weights for the active box
        setTimeout(() => updateBoxWeights(activeBoxIndex), 0);
        
        return updatedBoxes;
      });

    } catch (err) {
      console.error(err);
      alert('Error finding item');
    }
  }, [activeBoxIndex]);

  /* =========================
     LOT SCAN (for active box)
     ========================= */
  const processLotScan = useCallback(
    (lotCode) => {
      if (activeItemIndex === null) {
        alert('No active item for lot code. Scan an item first.');
        setScanMode('ITEM');
        return;
      }

      setBoxes((prevBoxes) => {
        const updatedBoxes = [...prevBoxes];
        const currentBox = updatedBoxes[activeBoxIndex];
        
        if (!currentBox) return prevBoxes;
        
        const updatedItems = [...currentBox.items];
        
        if (updatedItems[activeItemIndex]) {
          const currentLotCode = updatedItems[activeItemIndex].lot_code;
          updatedItems[activeItemIndex] = {
            ...updatedItems[activeItemIndex],
            lot_code: currentLotCode
              ? `${currentLotCode}, ${lotCode}`
              : lotCode,
          };
        }

        updatedBoxes[activeBoxIndex] = {
          ...currentBox,
          items: updatedItems,
        };

        return updatedBoxes;
      });

      setActiveItemIndex(null);
      setScanMode('ITEM');
    },
    [activeBoxIndex, activeItemIndex]
  );

  /* =========================
     CHECK IF CODE IS AN ITEM (using local cache only - NO API CALL)
     ========================= */
  const checkIfItemCode = useCallback((code) => {
    // Only check local cache for active items - NO API CALL for lot codes
    return itemList.some(item => item.item_code === code && item.status === 0);
  }, [itemList]);

  /* =========================
     BARCODE LISTENER - FIXED VERSION (NO API CALLS FOR LOT CODES)
     ========================= */
  useEffect(() => {
    const handler = (e) => {
      // Don't process scans if not editable
      if (!isEditable) return;
      
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      if (e.key.length === 1) {
        bufferRef.current += e.key;
      }
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(async () => {
        if (processingRef.current) return;
        processingRef.current = true;

        const code = bufferRef.current.trim();
        bufferRef.current = '';

        if (code.length < 4) {
          processingRef.current = false;
          return;
        }

        console.log(`Scanned: ${code}, Current mode: ${scanMode}`);
        
        const isItem = checkIfItemCode(code);
        
        try {
          if (isItem) {
            await processItemScan(code);
          } else {
            if (scanMode === 'LOT' || activeItemIndex !== null) {
              processLotScan(code);
            } else {
              alert('Item not found');
            }
          }
        } catch (error) {
          console.error('Scan error:', error);
        } finally {
          processingRef.current = false;
        }
      }, 80);
    };

    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      clearTimeout(timeoutRef.current);
    };
  }, [scanMode, processItemScan, processLotScan, checkIfItemCode, isEditable]);

  /* =========================
     MANUAL ROW (for active box)
     ========================= */
  const addManualRow = () => {
    setBoxes((prevBoxes) => {
      const updatedBoxes = [...prevBoxes];
      const currentBox = updatedBoxes[activeBoxIndex];
      
      if (!currentBox) return prevBoxes;
      
      const newItems = [...currentBox.items];
      newItems.push({
        item_code: '',
        uom: '',
        weight: 0,
        qty: 1,
        total_weight: 0,
        lot_code: '',
        is_manual: true,
      });

      updatedBoxes[activeBoxIndex] = {
        ...currentBox,
        items: newItems,
      };

      return updatedBoxes;
    });
  };

  const handleManualItemChange = (index, code) => {
    const item = itemList.find((i) => i.item_code === code);
    if (!item) return;

    setBoxes((prevBoxes) => {
      const updatedBoxes = [...prevBoxes];
      const currentBox = updatedBoxes[activeBoxIndex];
      
      if (!currentBox) return prevBoxes;
      
      const updatedItems = [...currentBox.items];
      
      updatedItems[index] = {
        ...updatedItems[index],
        item_code: item.item_code,
        uom: item.uom,
        weight: Number(item.weight),
        total_weight: Number(item.weight) * updatedItems[index].qty,
      };

      updatedBoxes[activeBoxIndex] = {
        ...currentBox,
        items: updatedItems,
      };

      // Recalculate weights for the active box
      setTimeout(() => updateBoxWeights(activeBoxIndex), 0);

      return updatedBoxes;
    });
  };

  const handleManualLotChange = (index, value) => {
    setBoxes((prevBoxes) => {
      const updatedBoxes = [...prevBoxes];
      const currentBox = updatedBoxes[activeBoxIndex];
      
      if (!currentBox) return prevBoxes;
      
      const updatedItems = [...currentBox.items];
      updatedItems[index] = {
        ...updatedItems[index],
        lot_code: value,
      };

      updatedBoxes[activeBoxIndex] = {
        ...currentBox,
        items: updatedItems,
      };

      return updatedBoxes;
    });
  };

    useEffect(() => {
    const filtered = itemList.filter((i) =>
      i.item_code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const end = page * PAGE_SIZE;

    const nextItems = filtered.slice(0, end);

    const options = nextItems.map((i) => ({
      value: i.item_code,
      label: i.item_code,
    }));

    console.log("Rendering options:", options);

    setVisibleItems(options);
  }, [itemList, page, searchTerm]);

  const handleScrollBottom = () => {
    const filtered = itemList.filter((i) =>
      i.item_code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (page * PAGE_SIZE < filtered.length) {
      setPage((prev) => prev + 1);
    }
  };


    const handleManualQtyChange = (index, value) => {
      const qty = Number(value) || 1;
      
      setBoxes((prevBoxes) => {
        const updatedBoxes = [...prevBoxes];
        const currentBox = updatedBoxes[activeBoxIndex];
        
        if (!currentBox) return prevBoxes;
        
        const updatedItems = [...currentBox.items];
        updatedItems[index] = {
          ...updatedItems[index],
          qty,
          total_weight: updatedItems[index].weight * qty,
        };

        updatedBoxes[activeBoxIndex] = {
          ...currentBox,
          items: updatedItems,
        };

        return updatedBoxes;
      });
    };

    const deleteRow = (index) => {
      setBoxes((prevBoxes) => {
        const updatedBoxes = [...prevBoxes];
        const currentBox = updatedBoxes[activeBoxIndex];
        if (!currentBox) return prevBoxes;
        const newItems = currentBox.items.filter((_, i) => i !== index);
        updatedBoxes[activeBoxIndex] = {
          ...currentBox,
          items: newItems,
        };
        return updatedBoxes;
      });
      if (activeItemIndex === index) {
        setActiveItemIndex(null);
        setScanMode('ITEM');
      }
    };

  /* =========================
     LOAD EXISTING SLIP (EDIT MODE)
     ========================= */
  useEffect(() => {
    if (!id) return;

    const fetchSlip = async () => {
      try {
        const res = await apiFetch(
          `/api/packaging-slip/view-packaging-slip?id=${id}`
        );
        
        if (!res?.success) {
          alert(res?.message || 'Failed to load packaging slip');
          return;
        }
        
        setHeader(res.header);
        setBoxInfo(res.boxInfo);
        
        // Use the boxes data from the API response if available
        // Otherwise fall back to the old logic for backward compatibility
        if (res.boxes && res.boxes.length > 0) {
          // Use the new boxes structure from the API
          const loadedBoxes = res.boxes.map(box => ({
            boxNumber: box.boxNumber,
            length: box.length || '',
            width: box.width || '',
            height: box.height || '',
            net_weight: box.net_weight || 0,
            additional_weight: (box.gross_weight || 0) - (box.net_weight || 0),
            gross_weight: box.gross_weight || 0,
            box_type: box.box_type || '',
            items: box.items.map(item => ({
              item_code: item.item_code,
              uom: item.uom,
              weight: Number(item.weight),
              qty: Number(item.qty),
              total_weight: Number(item.total_weight),
              lot_code: item.lot_code,
              is_manual: item.is_manual || false,
            })),
          }));
          
          setBoxes(loadedBoxes);
          
          // Update box_count to match the actual number of boxes from API
          if (res.boxInfo.box_count !== loadedBoxes.length) {
            setBoxInfo(prev => ({
              ...prev,
              box_count: loadedBoxes.length,
            }));
          }
        } else {
          // Fallback: Use the old logic for backward compatibility
          // (All items go in the first box)
          const boxCount = res.boxInfo.box_count || 1;
          const loadedBoxes = [];
          
          for (let i = 0; i < boxCount; i++) {
            // All items go in the first box for backward compatibility
            const boxItems = i === 0 ? res.items : [];
            
            loadedBoxes.push({
              boxNumber: i + 1,
              length: res.boxInfo?.length || '',
              width: res.boxInfo?.width || '',
              height: res.boxInfo?.height || '',
              items: boxItems.map((item) => ({
                item_code: item.item_code,
                uom: item.uom,
                weight: Number(item.weight),
                qty: Number(item.qty),
                total_weight: Number(item.total_weight),
                lot_code: item.lot_code,
                is_manual: item.is_manual || false,
              })),
            });
          }
          
          setBoxes(loadedBoxes);
        }
        
      } catch (err) {
        console.error(err);
        alert('Failed to load packaging slip');
      }
    };

    fetchSlip();
  }, [id]);
  
  /* =========================
     CANCEL PACKAGING SLIP
     ========================= */
  const [isCancelling, setIsCancelling] = useState(false);

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this packaging slip? This action cannot be undone.')) {
      return;
    }

    setIsCancelling(true);
    try {
      const res = await apiFetch('/api/packaging-slip/cancel-packaging-slip', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });

      if (res.success) {
        alert('Packaging slip cancelled successfully');
        setHeader(prev => ({ ...prev, status: 'cancel' }));
      } else {
        alert(res.message || 'Cancel failed');
      }
    } catch (error) {
      console.error('Cancel error:', error);
      alert('Cancel failed');
    } finally {
      setIsCancelling(false);
    }
  };


  /* =========================
     SAVE
     ========================= */
  const handleSave = async () => {
    if (!header.invoice_no || !header.invoice_date || !header.customer_name) {
      alert('Required fields missing');
      return;
    }
    // Check if any box has items
    const hasItems = boxes.some(box => box.items.length > 0);
    if (!hasItems) {
      alert('Add at least one item to any box');
      return;
    }
    setIsSaving(true);
    try {
      const allItems = boxes.flatMap(box => box.items);
      
      // Calculate and include weight data for each box
      const boxesWithWeight = boxes.map(box => ({
        ...box,
        net_weight: box.net_weight,
        gross_weight: box.gross_weight,
      }));
      
      const res = await apiFetch('/api/packaging-slip/save-packaging-slip', {
        method: 'POST',
        body: JSON.stringify({
          id: id || null,
          header,
          boxInfo,
          items: allItems,
          boxes: boxesWithWeight,
        }),
      });
      if (res.success) {
        alert(isEdit ? 'Updated successfully' : 'Saved successfully');
        navigate('/packagin-index');
      } else {
        alert(res.message || 'Save failed');
      }
    } catch {
      alert('Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  /* =========================
     DOWNLOAD PDF
     ========================= */
  const handleDownloadPdf = async () => {
    if (!id || !header.invoice_no) {
      alert('Cannot download PDF: Packaging slip data not available');
      return;
    }

    try {
      setDownloadingPdf(true);
      
      const blob = await downloadPdf(`/pdf-packaging.php?id=${id}`);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `Packing_List_${header.invoice_no}.pdf`;
      
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('PDF download failed:', error);
      alert(`PDF download failed: ${error.message}`);
    } finally {
      setDownloadingPdf(false);
    }
  };

  /* =========================
     RESET SCAN STATE WHEN SWITCHING BOXES
     ========================= */
  useEffect(() => {
    setActiveItemIndex(null);
    setScanMode('ITEM');
  }, [activeBoxIndex]);

  /* =========================
     RECALCULATE WEIGHTS WHEN ITEMS CHANGE
     ========================= */
  useEffect(() => {
    boxes.forEach((box, index) => {
      const calculatedNetWeight = calculateNetWeight(index);
      if (box.net_weight !== calculatedNetWeight) {
        updateBoxWeights(index);
      }
    });
  }, [boxes.map(box => box.items.length).join(',')]);

  /* =========================
     RENDER
     ========================= */
  return (
    <Layout>
      <div style={styles.pageContainer}>
        <div style={styles.headerSection}>
          <div>
            <h2 style={styles.pageTitle}>
              {isEdit ? 'View / Edit Packaging Slip' : 'Create Packaging Slip'}
            </h2>
            {isEdit && header.status && (
              <div style={{
                display: 'inline-block',
                marginTop: '8px',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                backgroundColor: ['approved', 'submit'].includes(header.status) ? '#d1fae5' : '#fee2e2',
                color: ['approved', 'submit'].includes(header.status) ? '#065f46' : '#991b1b',
              }}>
                Status: {['approved', 'submit'].includes(header.status) ? 'Approved' : 'Cancelled'}
              </div>
            )}
            {!isEditable && (
              <div style={{
                marginTop: '8px',
                padding: '6px 12px',
                backgroundColor: '#fef3c7',
                color: '#92400e',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                display: 'inline-block',
              }}>
                🔒 Read-only mode - {['approved', 'submit'].includes(header.status) ? 'Approved' : 'Cancelled'} packaging slips cannot be edited
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {/* PDF Download Button - only show in edit mode */}
            {isEdit && header.invoice_no && (
              <button
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                style={{
                  padding: '10px 20px',
                  backgroundColor: downloadingPdf ? '#93c5fd' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: downloadingPdf ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: downloadingPdf ? 0.8 : 1,
                }}
              >
                {downloadingPdf ? (
                  <>
                    <span style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }}></span>
                    Downloading PDF...
                  </>
                ) : (
                  <>
                    📄 Download PDF
                  </>
                )}
              </button>
            )}

            {/* Cancel Button - only show for submitted slips */}
            {canCancel && (
              <button
                onClick={handleCancel}
                disabled={isCancelling}
                style={{
                  padding: '10px 20px',
                  backgroundColor: isCancelling ? '#fca5a5' : '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isCancelling ? 'not-allowed' : 'pointer',
                  opacity: isCancelling ? 0.6 : 1,
                }}
              >
                {isCancelling ? '⏳ Cancelling...' : '❌ Cancel Slip'}
              </button>
            )}

            {/* Save/Update Button - only show if editable */}
            {isEditable && (
              <button
                onClick={handleSave}
                style={styles.saveButton}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : isEdit ? 'Update' : 'Save'}
              </button>
            )}
          </div>
        </div>

        {/* ================= INVOICE DETAILS ================= */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              <span style={{ fontSize: '20px', marginRight: '8px' }}>📄</span>
              Shipment Details
              {header.invoice_no && (
                <span style={{
                  marginLeft: '12px',
                  fontSize: '14px',
                  fontWeight: 'normal',
                  color: '#6b7280',
                  backgroundColor: '#f3f4f6',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontFamily: 'monospace',
                }}>
                  #{header.invoice_no}
                </span>
              )}
            </h3>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '13px',
              color: '#6b7280',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                backgroundColor: '#f8fafc',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
              }}>
                <span>📅</span>
                <span>Date: <DateTimeText value={header.invoice_date || 'Not set'} /></span>
              </div>
            </div>
          </div>

          {/* Form Grid with Improved Layout */}
          <div style={styles.formGrid}>
            {/* Invoice Number */}
            <div>
              <label style={styles.labelStyle}>
                <span style={{ color: '#ef4444' }}>*</span>
                Shipment / DC Number
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  name="invoice_no"
                  value={header.invoice_no}
                  onChange={handleHeaderChange}
                  disabled={!isEditable}
                  style={{
                    ...styles.inputStyle,
                    paddingLeft: '40px',
                    fontWeight: header.invoice_no ? '600' : 'normal',
                    color: header.invoice_no ? '#1e40af' : '#374151',
                    backgroundColor: !isEditable ? '#f9fafb' : 'white',
                    cursor: !isEditable ? 'not-allowed' : 'text',
                  }}
                  placeholder="Enter invoice number"
                />
                <span style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '16px',
                  color: '#9ca3af',
                }}>
                  #
                </span>
              </div>
              {!header.invoice_no && (
                <div style={styles.fieldHint}>
                  Required field
                </div>
              )}
            </div>

            {/* Invoice Date */}
            <div>
              <label style={styles.labelStyle}>
                <span style={{ color: '#ef4444' }}>*</span>
                Shipment / DC Date & Time
              </label>
              <div style={{ position: 'relative' }}>
                {!isEditable ? (
                  <div style={{
                    ...styles.inputStyle,
                    paddingLeft: '40px',
                    color: '#1e40af',
                    fontWeight: '600',
                    backgroundColor: '#f9fafb',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    <span style={{ position: 'absolute', left: '12px', fontSize: '16px', color: '#9ca3af' }}>📅</span>
                    {header.invoice_date ? (() => {
                      const d = new Date(header.invoice_date.replace(' ', 'T'));
                      return d.toLocaleDateString('en-GB') + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
                    })() : '-'}
                  </div>
                ) : (
                  <>
                    <div
                      onClick={() => invoiceDateRef.current?.showPicker()}
                      style={{
                        ...styles.inputStyle,
                        paddingLeft: '40px',
                        color: header.invoice_date ? '#1e40af' : '#9ca3af',
                        fontWeight: header.invoice_date ? '600' : 'normal',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#9ca3af', pointerEvents: 'none' }}>📅</span>
                      {header.invoice_date ? (() => {
                        const d = new Date(header.invoice_date.replace(' ', 'T'));
                        return d.toLocaleDateString('en-GB') + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
                      })() : 'Select date & time'}
                    </div>
                    <input
                      ref={invoiceDateRef}
                      type="datetime-local"
                      name="invoice_date"
                      value={header.invoice_date}
                      onChange={handleHeaderChange}
                      style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                    />
                  </>
                )}
              </div>
            </div>

            {/* Customer Name */}
            <div>
              <label style={styles.labelStyle}>
                <span style={{ color: '#ef4444' }}>*</span>
                Customer Name
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  name="customer_name"
                  value={header.customer_name}
                  onChange={handleHeaderChange}
                  style={{
                    ...styles.inputStyle,
                    paddingLeft: '40px',
                    color: header.customer_name ? '#1e40af' : '#374151',
                    fontWeight: header.customer_name ? '600' : 'normal',
                  }}
                  placeholder="Enter customer name"
                />
                <span style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '16px',
                  color: '#9ca3af',
                }}>
                  👤
                </span>
              </div>
            </div>

            {/* Order Number */}
            <div>
              <label style={styles.labelStyle}>
                Order Number
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  name="order_no"
                  value={header.order_no}
                  onChange={handleHeaderChange}
                  style={{
                    ...styles.inputStyle,
                    paddingLeft: '40px',
                    color: header.order_no ? '#059669' : '#374151',
                    fontWeight: header.order_no ? '600' : 'normal',
                  }}
                  placeholder="Enter order number"
                />
                <span style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '16px',
                  color: '#9ca3af',
                }}>
                  📋
                </span>
              </div>
            </div>

            {/* Order Date */}
            <div>
              <label style={styles.labelStyle}>
                Order Date
              </label>
              <div style={{ position: 'relative' }}>
                {!isEditable ? (
                  <div style={{
                    ...styles.inputStyle,
                    paddingLeft: '40px',
                    color: '#059669',
                    fontWeight: '600',
                    backgroundColor: '#f9fafb',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    <span style={{ position: 'absolute', left: '12px', fontSize: '16px', color: '#9ca3af' }}>📅</span>
                    {header.order_date ? new Date(header.order_date).toLocaleDateString('en-GB') : '-'}
                  </div>
                ) : (
                  <>
                    <div
                      onClick={() => orderDateRef.current?.showPicker()}
                      style={{
                        ...styles.inputStyle,
                        paddingLeft: '40px',
                        color: header.order_date ? '#059669' : '#9ca3af',
                        fontWeight: header.order_date ? '600' : 'normal',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#9ca3af', pointerEvents: 'none' }}>📅</span>
                      {header.order_date ? new Date(header.order_date).toLocaleDateString('en-GB') : 'Select date'}
                    </div>
                    <input
                      ref={orderDateRef}
                      type="date"
                      name="order_date"
                      value={header.order_date}
                      onChange={handleHeaderChange}
                      style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                    />
                  </>
                )}
              </div>
            </div>

            {/* Transport Mode */}
            <div>
              <label style={styles.labelStyle}>
                Transport Mode
              </label>
              <div style={{ position: 'relative' }}>
                <select
                  name="transport_mode"
                  value={header.transport_mode}
                  onChange={handleHeaderChange}
                  style={{
                    ...styles.inputStyle,
                    paddingLeft: '40px',
                    color: header.transport_mode ? '#059669' : '#374151',
                    fontWeight: header.transport_mode ? '600' : 'normal',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">Select transport mode</option>
                  <option value="by_air">By Air</option>
                  <option value="by_sea">By Sea</option>
                  <option value="by_road">By Road</option>
                </select>
                <span style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '16px',
                  color: '#9ca3af',
                  pointerEvents: 'none',
                }}>
                  {header.transport_mode === 'by_air' ? '✈️' : 
                   header.transport_mode === 'by_sea' ? '�' : 
                   header.transport_mode === 'by_road' ? '🚚' : '🚚'}
                </span>
              </div>
            </div>

            {/* Address (Full width) */}
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '8px',
              }}>
                <label style={styles.labelStyle}>
                  Customer Address
                </label>
              </div>
              <div style={{ position: 'relative' }}>
                <textarea
                  style={{
                    ...styles.textareaStyle,
                    paddingLeft: '40px',
                    minHeight: '100px',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    color: header.address ? '#374151' : '#9ca3af',
                  }}
                  rows="4"
                  name="address"
                  value={header.address}
                  onChange={handleHeaderChange}
                  placeholder="Enter customer address..."
                />
                <span style={{
                  position: 'absolute',
                  left: '12px',
                  top: '12px',
                  fontSize: '16px',
                  color: '#9ca3af',
                }}>
                  📍
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ================= COMPACT BOX TABS ================= */}
        {/* ================= BOXES CONTAINER (Box Count + Box Tabs) ================= */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              📦 Boxes
              <span style={{
                marginLeft: '12px',
                fontSize: '14px',
                fontWeight: 'normal',
                color: '#6b7280',
                backgroundColor: '#f3f4f6',
                padding: '4px 12px',
                borderRadius: '20px',
              }}>
                {boxes.length} box{boxes.length !== 1 ? 'es' : ''}
              </span>
            </h3>
            
            {/* Box Count Control */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              backgroundColor: '#f8fafc',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
            }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                whiteSpace: 'nowrap',
              }}>
                Number of Boxes:
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <button
                  onClick={() => {
                    const newCount = Math.max(1, boxInfo.box_count - 1);
                    handleBoxChange({ target: { name: 'box_count', value: newCount } });
                  }}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    color: '#374151',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  -
                </button>
                <input
                  type="number"
                  name="box_count"
                  min="1"
                  value={boxInfo.box_count}
                  onChange={handleBoxChange}
                  style={{
                    width: '70px',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    textAlign: 'center',
                    backgroundColor: 'white',
                  }}
                />
                <button
                  onClick={() => {
                    const newCount = boxInfo.box_count + 1;
                    handleBoxChange({ target: { name: 'box_count', value: newCount } });
                  }}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    color: '#374151',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  +
                </button>
              </div>
              <div style={{
                fontSize: '13px',
                color: '#6b7280',
                backgroundColor: '#f1f5f9',
                padding: '4px 10px',
                borderRadius: '6px',
              }}>
                Total Items: {boxes.reduce((total, box) => total + box.items.length, 0)}
              </div>
              <div style={{
                fontSize: '13px',
                color: '#6b7280',
                backgroundColor: '#ecfdf5',
                padding: '4px 10px',
                borderRadius: '6px',
                fontWeight: '600',
              }}>
                Total Weight: {boxes.reduce((total, box) => 
                  total + box.items.reduce((boxTotal, item) => boxTotal + (item.total_weight || 0), 0), 0
                ).toFixed(2)} kg
              </div>
            </div>
          </div>

          {/* Box Tabs Container */}
          <div style={{
            position: 'relative',
            marginTop: '16px',
          }}>
            {/* Scrollable box tabs */}
            <div style={{
              ...styles.tabsContainer,
              ...styles.customScrollbar,
              padding: '12px 4px',
              margin: '0',
            }}>
              {boxes.map((box, index) => {
                const isActive = index === activeBoxIndex;
                const hasItems = box.items.length > 0;
                
                return (
                  <div key={box.boxNumber} style={styles.boxTabContainer(isActive)}>
                    {/* Active Box Indicator */}
                    {isActive && (
                      <div style={styles.activeBoxIndicator}>
                        ✓
                      </div>
                    )}
                    
                    {/* Box Header with Title on Border */}
                    <div style={styles.boxHeader(isActive)}>
                      <div style={styles.boxTitle(isActive)}>
                        <span style={styles.boxNumberBadge(isActive)}>
                          {box.boxNumber}
                        </span>
                        <div style={{ flex: 1, textAlign: 'center' }}>
                          Box {box.boxNumber}
                        </div>
                        {hasItems && (
                          <span style={styles.itemCountBadge}>
                            {box.items.length}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Vertical Dimensions Inputs */}
                    <div style={styles.verticalDimensionsContainer(isActive)}>
                      {/* Length */}
                      <div style={styles.dimensionRow}>
                        <span style={{...styles.dimensionLabelCompact, width: '50px'}}>L</span>
                        <input
                          type="number"
                          name="length"
                          value={box.length}
                          onChange={(e) => handleBoxDimensionChange(index, e)}
                          style={styles.dimensionInputCompact(isActive)}
                          placeholder="0"
                        />
                        <span style={styles.dimensionUnit}>mm</span>
                      </div>
                      
                      {/* Width */}
                      <div style={styles.dimensionRow}>
                        <span style={{...styles.dimensionLabelCompact, width: '50px'}}>W</span>
                        <input
                          type="number"
                          name="width"
                          value={box.width}
                          onChange={(e) => handleBoxDimensionChange(index, e)}
                          style={styles.dimensionInputCompact(isActive)}
                          placeholder="0"
                        />
                        <span style={styles.dimensionUnit}>mm</span>
                      </div>
                      
                      {/* Height */}
                      <div style={styles.dimensionRow}>
                        <span style={{...styles.dimensionLabelCompact, width: '50px'}}>H</span>
                        <input
                          type="number"
                          name="height"
                          value={box.height}
                          onChange={(e) => handleBoxDimensionChange(index, e)}
                          style={styles.dimensionInputCompact(isActive)}
                          placeholder="0"
                        />
                        <span style={styles.dimensionUnit}>mm</span>
                      </div>
                      
                      {/* Net Weight - Auto-calculated */}
                      <div style={styles.dimensionRow}>
                        <span style={{...styles.dimensionLabelCompact, width: '50px'}}>Net</span>
                        <input
                          type="number"
                          value={box.net_weight.toFixed(2)}
                          style={{
                            ...styles.dimensionInputCompact(isActive),
                            backgroundColor: '#f3f4f6',
                            color: '#6b7280',
                            cursor: 'not-allowed',
                          }}
                          placeholder="0"
                          readOnly
                          title="Auto-calculated from item weights"
                        />
                        <span style={styles.dimensionUnit}>kg</span>
                      </div>
                      
                      {/* Additional Weight - Manual input */}
                      <div style={styles.dimensionRow}>
                        <span style={{...styles.dimensionLabelCompact, width: '50px'}}>Gross</span>
                        <input
                          type="number"
                          step="0.01"
                            value={box.additional_weight ?? ""}
                          onChange={(e) => handleAdditionalWeightChange(index, e.target.value)}
                          style={styles.dimensionInputCompact(isActive)}
                          placeholder=""
                          title="Additional weight (packaging, etc.)"
                        />
                        <span style={styles.dimensionUnit}>kg</span>
                      </div>
                      
                      {/* Gross Weight - Auto-calculated */}
                      {/* <div style={styles.dimensionRow}>
                        <span style={{...styles.dimensionLabelCompact, width: '50px', fontWeight: 'bold'}}>Gross</span>
                        <input
                          type="number"
                          value={box.gross_weight.toFixed(2)}
                          style={{
                            ...styles.dimensionInputCompact(isActive),
                            backgroundColor: '#e0f2fe',
                            color: '#0c4a6e',
                            cursor: 'not-allowed',
                            fontWeight: 'bold',
                          }}
                          placeholder="0"
                          readOnly
                          title="Net weight + Additional weight"
                        />
                        <span style={styles.dimensionUnit}>kg</span>
                      </div> */}
                      
                      {/* Box Type - Dropdown */}
                      <div style={styles.dimensionRow}>
                        <span style={{...styles.dimensionLabelCompact, width: '50px'}}>Type</span>
                        <select
                          value={box.box_type}
                          onChange={(e) => handleBoxTypeChange(index, e.target.value)}
                          disabled={!isEditable}
                          style={{
                            ...styles.dimensionInputCompact(isActive),
                            backgroundColor: !isEditable ? '#f9fafb' : 'white',
                            cursor: !isEditable ? 'not-allowed' : 'pointer',
                          }}
                          title="Select type of box"
                        >
                          <option value="">Select Box Type</option>
                          <option value="Carton Box">Carton Box</option>
                          <option value="Wooden Box">Wooden Box</option>
                          <option value="EPE Foam Packing">EPE Foam Packing</option>
                          <option value="Soft Packing">Soft Packing</option>
                        </select>
                        <span style={{...styles.dimensionUnit, width: '20px'}}></span>
                      </div>
                    </div>
                    
                    {/* Select Box Button */}
                    <button
                      style={{
                        ...styles.scanButton(isActive),
                        marginTop: '16px',
                        fontSize: '11px',
                        padding: '6px 10px',
                      }}
                      onClick={() => {
                        setActiveBoxIndex(index);
                      }}
                    >
                      {isActive ? '✓ Active' : 'Select Box'}
                    </button>
                    
                    {/* Quick item preview */}
                    {hasItems && (
                      <div style={{
                        marginTop: '10px',
                        padding: '6px',
                        backgroundColor: isActive ? '#e0f2fe' : '#f8fafc',
                        borderRadius: '4px',
                        fontSize: '10px',
                        color: isActive ? '#0369a1' : '#64748b',
                        border: `1px solid ${isActive ? '#7dd3fc' : '#e2e8f0'}`,
                      }}>
                        {/* Total Weight Display */}
                        <div style={{
                          textAlign: 'center',
                          fontWeight: 'bold',
                          marginBottom: '4px',
                          fontSize: '11px',
                          color: isActive ? '#0c4a6e' : '#475569',
                        }}>
                          Net: {box.net_weight.toFixed(2)} kg | Gross: {Number(box.additional_weight || 0).toFixed(2)} 
                           kg
                        </div>
                        
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '2px',
                          maxHeight: '30px',
                          overflow: 'hidden',
                          justifyContent: 'center',
                        }}>
                          {box.items.slice(0, 2).map((item, i) => (
                            <span key={i} style={{
                              padding: '1px 3px',
                              backgroundColor: isActive ? '#bae6fd' : '#f1f5f9',
                              borderRadius: '2px',
                              fontSize: '9px',
                              fontFamily: 'monospace',
                              whiteSpace: 'nowrap',
                            }}>
                              {item.item_code}
                            </span>
                          ))}
                          {box.items.length > 2 && (
                            <span style={{
                              padding: '1px 4px',
                              backgroundColor: isActive ? '#bae6fd' : '#f1f5f9',
                              borderRadius: '2px',
                              fontSize: '9px',
                              fontWeight: 'bold',
                              color: isActive ? '#0369a1' : '#64748b',
                            }}>
                              +{box.items.length - 2}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Scroll hint */}
            <div style={{
              textAlign: 'center',
              fontSize: '12px',
              color: '#9ca3af',
              marginTop: '12px',
              padding: '8px',
              backgroundColor: '#f9fafb',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}>
              <span>↔</span>
              <span>Scroll horizontally to see all boxes</span>
              <span>↔</span>
            </div>
          </div>

          {/* Active Box Status */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '20px',
            padding: '14px 16px',
            backgroundColor: '#f0f9ff',
            borderRadius: '8px',
            border: '2px solid #3b82f6',
            fontSize: '14px',
            flexWrap: 'wrap',
            gap: '12px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: '700', color: '#1e40af' }}>
                📦 Currently Active:
              </span>
              <span style={{
                padding: '6px 16px',
                backgroundColor: '#3b82f6',
                color: 'white',
                borderRadius: '20px',
                fontWeight: 'bold',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}>
                Box {activeBoxIndex + 1}
              </span>
              <span style={{ color: '#374151' }}>
                ({boxes[activeBoxIndex]?.items.length || 0} items, {
                  boxes[activeBoxIndex]?.items.reduce((total, item) => total + (item.total_weight || 0), 0).toFixed(2) || '0.00'
                } kg)
              </span>
            </div>
            <div style={{ 
              fontSize: '13px',
              color: '#6b7280',
              backgroundColor: 'white',
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #e5e7eb',
            }}>
              ⚡ Items will be scanned into this box
            </div>
          </div>
        </div>

        {/* ================= ALL ITEMS + SCAN STATUS IN ONE CONTAINER ================= */}
        <div style={styles.card}>
          {/* Card Header with Title and Add Button */}
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              📋 Items for Box {activeBoxIndex + 1}
              <span style={{
                marginLeft: '12px',
                fontSize: '14px',
                fontWeight: 'normal',
                color: '#6b7280',
                backgroundColor: '#f3f4f6',
                padding: '4px 12px',
                borderRadius: '20px',
              }}>
                {boxes[activeBoxIndex]?.items.length || 0} items
              </span>
            </h3>
            <button 
              onClick={addManualRow} 
              disabled={!isEditable}
              style={{
                ...styles.addButton,
                opacity: !isEditable ? 0.5 : 1,
                cursor: !isEditable ? 'not-allowed' : 'pointer',
              }}
            >
              + Add Item
            </button>
          </div>

          {/* ================= COMPACT SCAN STATUS BAR ================= */}
          <div style={{
            backgroundColor: '#f0f9ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
              {/* Scan Mode Indicator */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: scanMode === 'ITEM' ? '#10b981' : '#f59e0b',
                  animation: 'pulse 1.5s infinite',
                }} />
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                  Scan Mode:
                </span>
                <div style={scanMode === 'ITEM' ? {
                  padding: '6px 12px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                } : {
                  padding: '6px 12px',
                  backgroundColor: '#fef3c7',
                  color: '#92400e',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  border: '1px solid #fbbf24',
                }}>
                  {scanMode === 'ITEM' ? '📦 ITEM SCAN' : '🏷️ LOT CODE SCAN'}
                </div>
              </div>

              {/* Active Box Indicator */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                  Active Box:
                </span>
                <div style={{
                  padding: '6px 14px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  minWidth: '70px',
                  textAlign: 'center',
                }}>
                  Box {activeBoxIndex + 1}
                </div>
              </div>

              {/* Scan Status */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                  Status:
                </span>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: !isEditable ? '#ef4444' : scanMode === 'ITEM' ? '#059669' : '#92400e',
                }}>
                  {!isEditable ? `Editing disabled - Slip is ${header.status === 'submit' ? 'submitted' : 'cancelled'}` : 
                   scanMode === 'ITEM' ? 'Ready to scan items' : 'Awaiting lot code'}
                </span>
              </div>
            </div>

            {/* Scan Instructions */}
            <div style={{
              fontSize: '13px',
              color: '#6b7280',
              backgroundColor: 'white',
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #e5e7eb',
            }}>
              {!isEditable ? '🔒 Read-only mode' : '💡 Focus on page and scan barcodes'}
            </div>
          </div>

          {/* Active Item Alert (When waiting for lot code) */}
          {scanMode === 'LOT' && activeItemIndex !== null && boxes[activeBoxIndex]?.items[activeItemIndex] && (
            <div style={{
              backgroundColor: '#fef3c7',
              border: '2px solid #f59e0b',
              borderRadius: '8px',
              padding: '14px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              animation: 'pulse 2s infinite',
            }}>
              <div style={{ fontSize: '20px' }}>⏳</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
                  Awaiting lot code for:
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#b45309',
                  backgroundColor: '#fef9c3',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  marginTop: '4px',
                  display: 'inline-block',
                  fontFamily: 'monospace',
                }}>
                  {boxes[activeBoxIndex].items[activeItemIndex].item_code}
                </div>
              </div>
              <button
                onClick={() => {
                  setActiveItemIndex(null);
                  setScanMode('ITEM');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f1f5f9',
                  color: '#64748b',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                Skip Lot
              </button>
            </div>
          )}

          {/* ================= SCANNED ITEMS TABLE ================= */}
          <div style={styles.tableContainer}>
            <table style={styles.tableStyle}>
              <thead>
                <tr>
                  <th style={{...styles.thStyle, width: '20%'}}>Item Code</th>
                  <th style={{...styles.thStyle, width: '10%'}}>UOM</th>
                  <th style={{...styles.thStyle, width: '12%'}}>Weight / Unit</th>
                  <th style={{...styles.thStyle, width: '10%'}}>Qty</th>
                  <th style={{...styles.thStyle, width: '23%'}}>Lot Code</th>
                  <th style={{...styles.thStyle, width: '15%'}}>Total Weight</th>
                  <th style={{...styles.thStyle, width: '10%'}}>Action</th>
                </tr>
              </thead>
              <tbody>
                {boxes[activeBoxIndex]?.items.map((r, i) => (
                  <tr key={i} style={styles.trStyle(i === activeItemIndex)}>
                    <td style={styles.tdStyle}>
                      {r.is_manual ? (
                        <Select
                          options={visibleItems}
                          value={
                            r.item_code
                              ? { value: r.item_code, label: r.item_code }
                              : null
                          }
                          onChange={(selected) =>
                            handleManualItemChange(i, selected.value)
                          }
                          onMenuScrollToBottom={handleScrollBottom}
                          onInputChange={(input) => {
                            setSearchTerm(input);
                            setPage(1);
                          }}
                          placeholder="Search Item"
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: "32px",
                              fontSize: "14px",
                            }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}

                          menuPortalTarget={document.body}
                        />
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {i === activeItemIndex && (
                            <div
                              style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: '#3b82f6',
                                animation: 'pulse 1.5s infinite',
                              }}
                            />
                          )}
                          <strong>{r.item_code}</strong>
                        </div>
                      )}
                    </td>
                    <td style={styles.tdStyle}>
                      <span style={{
                        padding: '4px 8px',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: '500',
                      }}>
                        {r.uom}
                      </span>
                    </td>
                    <td style={styles.tdStyle}>
                      {r.weight}
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>kg/unit</div>
                    </td>
                    <td style={styles.tdStyle}>
                      {r.is_manual ? (
                        <input
                          type="number"
                          value={r.qty}
                          min="1"
                          onChange={(e) => handleManualQtyChange(i, e.target.value)}
                          style={styles.qtyInputStyle}
                        />
                      ) : (
                        <div style={styles.qtyBadge}>
                          {r.qty}
                        </div>
                      )}
                    </td>
                    <td style={styles.tdStyle}>
                      <div style={{
                        padding: '6px 10px',
                        backgroundColor: r.lot_code ? '#e0e7ff' : '#f3f4f6',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: r.lot_code ? '#3730a3' : '#9ca3af',
                        fontFamily: r.lot_code ? 'monospace' : 'inherit',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '200px',
                      }}>
                        {r.lot_code || 'No lot code'}
                      </div>
                    </td>
                    <td style={styles.tdStyle}>
                      <strong style={{ fontSize: '15px' }}>{r.total_weight.toFixed(2)}</strong>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>kg</div>
                    </td>
                    <td style={styles.tdStyle}>
                      <button
                        onClick={() => deleteRow(i)}
                        style={{
                            padding: '8px 12px',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = '#fecaca';
                          e.target.style.transform = 'translateY(-1px)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = '#fee2e2';
                          e.target.style.transform = 'translateY(0)';
                        }}
                      >
                          <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {(!boxes[activeBoxIndex]?.items || boxes[activeBoxIndex].items.length === 0) && (
              <div style={styles.emptyState}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
                <p style={{ fontSize: '16px', fontWeight: '500', color: '#4b5563' }}>
                  No items added to Box {activeBoxIndex + 1} yet
                </p>
                <p style={styles.emptyStateSubtext}>
                  Scan an item barcode or click "Add Item Manually" to add items
                </p>
                <div style={{
                  marginTop: '20px',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px dashed #d1d5db',
                  maxWidth: '500px',
                  margin: '20px auto 0',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '24px' }}>💡</span>
                    <strong style={{ color: '#374151' }}>How to add items:</strong>
                  </div>
                  <ol style={{
                    textAlign: 'left',
                    paddingLeft: '24px',
                    color: '#6b7280',
                    fontSize: '14px',
                    lineHeight: '1.6',
                  }}>
                    <li>Make sure <strong>Box {activeBoxIndex + 1}</strong> is active (highlighted in blue above)</li>
                    <li>Click anywhere on this page to focus</li>
                    <li>Scan item barcodes using your barcode scanner</li>
                    <li>Scanned items will appear automatically in this table</li>
                  </ol>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

/* =========================
   INPUT
   ========================= */
function Input({ label, ...props }) {
  return (
    <div>
      <label style={styles.labelStyle}>{label}</label>
      <input style={styles.inputStyle} {...props} />
    </div>
  );
}